// Shared "printed ticket" ornaments — the Anti-cardslop design language used by
// both the blend page and the solo dashboard: one ink-outlined sheet whose
// sections are fields divided by perforated tear-lines. No floating cards.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "./brand.tsx";

/** The paper frame + white ink-outlined sheet. */
export function Sheet({ children, testid }: { children: ReactNode; testid?: string }) {
  return (
    <div data-testid={testid} className="desk">
      <div className="sheet fadeup">{children}</div>
    </div>
  );
}

/** Masthead: wordmark + a mono kicker on the right (e.g. "Admit two"). */
export function Masthead({ right, sub }: { right?: ReactNode; sub?: ReactNode }) {
  return (
    <>
      <div className="flex items-center justify-between px-6 py-4 sm:px-10">
        <Link to="/" aria-label="twoshot home" className="inline-flex">
          <Wordmark size={17} />
        </Link>
        <div className="flex items-baseline gap-4">
          {right && <span className="font-mono text-[11px] font-bold uppercase tracking-stamp text-red-600">{right}</span>}
          {sub && <span className="hidden font-mono text-[11px] uppercase tracking-stamp text-ink-400 sm:inline">{sub}</span>}
        </div>
      </div>
      <div className="mx-6 h-px bg-border-soft sm:mx-10" />
    </>
  );
}

/** A perforated tear-line divider with punched notches at the edges. */
export function Tear() {
  return (
    <div className="tdiv" aria-hidden>
      <span className="ntch nl" />
      <div className="perf" />
      <span className="ntch nr" />
    </div>
  );
}

/** A padded field on the sheet. */
export function Field({ testid, children, className = "" }: { testid?: string; children: ReactNode; className?: string }) {
  return (
    <section data-testid={testid} className={`px-6 py-7 sm:px-10 ${className}`}>
      {children}
    </section>
  );
}

export function Kicker({ children }: { children: ReactNode }) {
  return <div className="font-mono text-[11px] font-bold uppercase tracking-stamp text-red-600">{children}</div>;
}
export function H3({ children, inline }: { children: ReactNode; inline?: boolean }) {
  return (
    <h2 className={`${inline ? "" : "mt-1.5"} font-display text-xl font-extrabold tracking-tightish text-ink-900 sm:text-2xl`}>
      {children}
    </h2>
  );
}
export function Sub({ children }: { children: ReactNode }) {
  return <p className="mt-1 text-sm text-ink-500">{children}</p>;
}
export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="mt-3 rounded-lg border border-dashed border-border-strong bg-paper-50 p-4 text-sm text-ink-500">{children}</p>
  );
}
export function Barcode({ className = "" }: { className?: string }) {
  return <div className={`barcode ${className}`} aria-hidden />;
}

/** A big display ledger figure with a mono label (counts up when `n` animates). */
export function Count({ n, label, tone = "ink" }: { n: number; label: string; tone?: "ink" | "red" | "gold" | "clay" | "muted" }) {
  const color =
    tone === "red"
      ? "text-red-600"
      : tone === "gold"
        ? "text-gold-600"
        : tone === "clay"
          ? "text-clay-500"
          : tone === "muted"
            ? "text-ink-300"
            : "text-ink-900";
  return (
    <div>
      <div className={`font-display text-[32px] font-extrabold leading-none tracking-tightish tabular-nums ${color}`}>
        {typeof n === "number" ? n.toLocaleString() : n}
      </div>
      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-stamp text-ink-400">{label}</div>
    </div>
  );
}
